import type { RouteRecordRaw } from 'vue-router';

// The issues page is team-scoped, and the team lives in the path
// (Linear-style): `/team/core/issues`. `:teamKey` is the team's short key,
// lowercased, when it has one, its id otherwise (`Team.key` is nullable).
// `useTeamSelection` matches it case-insensitively and rewrites a non-canonical
// spelling (an old `/team/CORE/...` link) to the lowercase form.
export const teamIssuesRoute: RouteRecordRaw = {
  path: '/team/:teamKey/issues',
  name: 'TeamIssues',
  component: () => import('./pages/IssuesPage.vue'),
  // Sprints used to be a tab here (`?view=sprints`); it is now its own
  // team-scoped route. Forward old bookmarks, keeping the rest of the query.
  beforeEnter: (to) => {
    if (to.query.view === 'sprints') {
      return {
        name: 'TeamSprints',
        params: to.params,
        query: { ...to.query, view: undefined },
        replace: true,
      };
    }
  },
  meta: {
    title: 'Issues',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('tm:core:issues:list'),
  },
};

// A saved view of the team's issues. The SAME page component as
// `teamIssuesRoute` on purpose: a view is the issues list with a stored filter
// set and its own preference scope, not a different screen. `:slug` is
// `<kebab-name>-<slugId>`; only the trailing `slugId` is authoritative, so a
// rename keeps old links working.
//
// Deliberately NOT added to `TEAM_SCOPED_ROUTES` (`useTeamSelection.ts`):
// `selectTeam()` would then keep the user on this route while pushing
// `params: { teamKey }` with no `:slug` — a navigation vue-router rejects. The
// existing fallback lands on `TeamIssues`, which is the intent: the view
// belongs to the old team, so switching team leaves it. It IS in
// `TEAM_CANONICAL_ROUTES` though, so an old upper-case link to a view settles
// on the lowercase `:teamKey` like every other team-scoped URL.
export const teamIssuesViewRoute: RouteRecordRaw = {
  path: '/team/:teamKey/views/:slug',
  name: 'TeamIssuesView',
  component: () => import('./pages/IssuesPage.vue'),
  meta: {
    title: 'Issues',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('tm:core:issues:list'),
  },
};

// The sprints collection is a separate team-scoped page (Linear Cycles
// analog), not a layout of the issues list — board/table stay tabs on the
// issues page, sprints gets its own path and sidebar entry.
export const teamSprintsRoute: RouteRecordRaw = {
  path: '/team/:teamKey/sprints',
  name: 'TeamSprints',
  component: () => import('./pages/SprintsPage.vue'),
  meta: {
    title: 'Sprints',
    layout: 'main',
    authorize: ({ isGranted }) =>
      isGranted('tm:core:issues:list') && isGranted('tm:core:sprints:list'),
  },
};

// Bare `/issues` (sidebar link, `{ name: 'Issues' }` navigations, bookmarks)
// resolves to the team-scoped URL: legacy `?team=<id>` when valid, else the
// last team remembered in localStorage, else the first (default) team. A
// static redirect can't do this — the team list is fetched async — so a
// resolver page picks the team and replaces to it (same pattern as
// `WorkflowStatusesRedirectPage`).
export const issuesRoute: RouteRecordRaw = {
  path: '/issues',
  name: 'Issues',
  component: () => import('./pages/IssuesRedirectPage.vue'),
  meta: {
    title: 'Issues',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('tm:core:issues:list'),
  },
};

// Bare `/sprints` resolves to the team-scoped URL exactly like bare `/issues`
// does — same async team resolution, so a resolver page (not a static
// redirect) picks the team and replaces to it.
export const sprintsRoute: RouteRecordRaw = {
  path: '/sprints',
  name: 'Sprints',
  component: () => import('./pages/SprintsRedirectPage.vue'),
  meta: {
    title: 'Sprints',
    layout: 'main',
    authorize: ({ isGranted }) =>
      isGranted('tm:core:issues:list') && isGranted('tm:core:sprints:list'),
  },
};

export const issueRoute: RouteRecordRaw = {
  // By the human-friendly global number, not the entity id — "#42" → /issues/42.
  path: '/issues/:number(\\d+)',
  name: 'Issue',
  component: () => import('./pages/IssuePage.vue'),
  meta: {
    title: 'Issue',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('tm:core:issues:get'),
  },
};

// The board is now the default view inside the merged Issues page. Keep the old
// /board URL working (bookmarks, links) by redirecting it there.
export const boardRedirectRoute: RouteRecordRaw = {
  path: '/board',
  redirect: { name: 'Issues' },
};

// Workflow statuses are now managed on the team detail page. Keep the old
// /settings/workflow-statuses URL working (bookmarks, links) by forwarding it to
// the relevant team. A static redirect can't do this correctly — the team list
// is fetched async, so a bare URL (no `?team=`) or a stale `?team=<id>` can't be
// resolved to the default team synchronously. The resolver page picks the team
// (query team when valid, else the first/default team) and replaces to it.
export const workflowStatusesRedirectRoute: RouteRecordRaw = {
  path: '/settings/workflow-statuses',
  name: 'WorkflowStatusesRedirect',
  component: () => import('./pages/WorkflowStatusesRedirectPage.vue'),
  meta: {
    title: 'Workflow Statuses',
    layout: 'main',
    authorize: ({ isGranted }) => isGranted('tm:core:teams:list'),
  },
};
